/**
 * Custom Text Pricing
 * Sync marker: custom-letter main deploy 2026-05-05.
 * - Counts typed letters (non-space chars) in Field 1
 * - Sets product quantity = letter count → AIOD tier breaks apply automatically
 * - Updates ".cs-price-label" to show "N letters"
 * - Patches "item" → "letter" in the unit price display
 * - Hides the manual quantity selector while active
 * - Flips the hidden "Color Type" variant to multi-color when a trigger field has text
 */
(function () {
  'use strict';

  class CustomTextPricing {
    constructor(root) {
      this.root        = root;
      this.countSpaces = root.dataset.countSpaces === 'true';
      this.fieldEls    = Array.from(root.querySelectorAll('[data-ctp-field]'));
      this.hiddenProps = [];

      this.productInfo = root.closest('product-info') || document.querySelector('product-info');
      this.sectionId = this.productInfo ? this.productInfo.dataset.section || '' : '';
      this.productForm = this._findForm();

      this._injectHiddenInputs();
      this._hydrateFromQuery();
      this._bindEvents();
      this.update();
      requestAnimationFrame(() => this.update());
      window.setTimeout(() => this.update(), 250);
    }

    // ── Setup ─────────────────────────────────────────────────────

    _findForm() {
      if (this.productInfo) {
        return (
          this.productInfo.querySelector('product-form form') ||
          this.productInfo.querySelector('form[action*="/cart/add"]') ||
          null
        );
      }

      return (
        document.querySelector('form[action*="/cart/add"]') ||
        document.querySelector('.product-form form') ||
        null
      );
    }

    _injectHiddenInputs() {
      if (!this.productForm) return;
      this.fieldEls.forEach((field, i) => {
        const propName = field.dataset.propertyName || ('Custom Text ' + (i + 1));
        const el = document.createElement('input');
        el.type = 'hidden';
        el.name = 'properties[' + propName + ']';
        this.productForm.appendChild(el);
        this.hiddenProps.push(el);
      });

      // Hidden marker so cart Liquid can identify CTP line items
      const ctpFlag = document.createElement('input');
      ctpFlag.type  = 'hidden';
      ctpFlag.name  = 'properties[_ctp]';
      ctpFlag.value = '1';
      this.productForm.appendChild(ctpFlag);

      // Visible letter count shown as a line item attribute in the cart
      this.lettersProp = document.createElement('input');
      this.lettersProp.type = 'hidden';
      this.lettersProp.name = 'properties[Letters]';
      this.productForm.appendChild(this.lettersProp);
    }

    _hydrateFromQuery() {
      const params = new URLSearchParams(window.location.search);
      const inputs = Array.from(this.root.querySelectorAll('[data-ctp-input]'));

      inputs.forEach((input, index) => {
        const paramName = 'ctp' + (index + 1);
        if (!params.has(paramName)) return;
        input.value = params.get(paramName) || '';
      });
    }

    _bindEvents() {
      // Text inputs
      this.root.querySelectorAll('[data-ctp-input]').forEach(el => {
        el.addEventListener('input', () => this.update());
      });

      // Watch [data-cs-unit] and patch "item" → "letter" after unit-price.js re-renders
      const unitEl = (this.productInfo && this.productInfo.querySelector('[data-cs-unit]')) || document.querySelector('[data-cs-unit]');
      if (unitEl) {
        new MutationObserver(() => this._patchUnitLabel(unitEl))
          .observe(unitEl, { childList: true, subtree: true, characterData: true });
      }

      // Validation on submit — listen to the custom validation event from product-form.js
      if (this.productForm) {
        this.productForm.addEventListener('product-form:validate', e => this._validateSubmit(e));
      }
    }

    // ── Helpers ───────────────────────────────────────────────────

    _billableChars(text) {
      return this.countSpaces ? text.length : text.replace(/\s/g, '').length;
    }

    _getQtyInput() {
      const scopedQtyInput = this.productInfo && (
        (this.sectionId && document.getElementById('Quantity-' + this.sectionId)) ||
        this.productInfo.querySelector('.quantity__input') ||
        this.productInfo.querySelector('[name="quantity"]')
      );

      return (
        scopedQtyInput ||
        document.querySelector('.quantity__input') ||
        document.querySelector('[name="quantity"]')
      );
    }

    _setQty(n) {
      const qtyInput = this._getQtyInput();
      if (!qtyInput) return;
      const min = this._getMinQty();
      const nextQty = Math.max(min, n);
      qtyInput.value = String(nextQty);
      qtyInput.setAttribute('value', String(nextQty));
      qtyInput.dataset.ctpQuantity = String(nextQty);
      ['input', 'change'].forEach(evt =>
        qtyInput.dispatchEvent(new Event(evt, { bubbles: true }))
      );
    }

    _getMinQty() {
      const qtyInput = this._getQtyInput();
      return qtyInput ? Math.max(1, parseInt(qtyInput.getAttribute('min') || qtyInput.dataset.min || '1', 10)) : 1;
    }

    _patchUnitLabel(unitEl) {
      if (!this.productInfo?.hasAttribute('data-ctp-active')) return;
      unitEl.querySelectorAll('span').forEach(span => {
        if (!span.children.length) {
          span.textContent = span.textContent
            .replace(/\bitems\b/gi, 'letters')
            .replace(/\bitem\b/gi, 'letter');
        }
      });
    }

    _updatePriceLabel(chars) {
      const labelEl = (this.productInfo && this.productInfo.querySelector('.cs-price-label')) || document.querySelector('.cs-price-label');
      if (!labelEl) return;
      const displayChars = chars > 0 ? chars : this._getMinQty();
      labelEl.textContent = displayChars + (displayChars === 1 ? ' letter' : ' letters');
    }

    _updateMinimumChargeNote(chars) {
      const priceBlock = (this.productInfo && this.productInfo.querySelector('.cs-price-block')) || document.querySelector('.cs-price-block');
      if (!priceBlock) return;

      let noteEl = priceBlock.querySelector('.cs-price-note');
      if (!noteEl) {
        noteEl = document.createElement('div');
        noteEl.className = 'cs-price-note';
        noteEl.style.marginTop = '8px';
        noteEl.style.fontSize = '1.15rem';
        noteEl.style.lineHeight = '1.4';
        noteEl.style.color = '#7a5a17';
        priceBlock.appendChild(noteEl);
      }

      const min = this._getMinQty();

      if (chars < min) {
        noteEl.textContent = chars > 0
          ? 'Minimum charge applies: priced as ' + min + ' letters.'
          : min + ' letter minimum.';
        noteEl.hidden = false;
      } else {
        noteEl.textContent = '';
        noteEl.hidden = true;
      }
    }

    // ── Core update ───────────────────────────────────────────────

    update() {
      let totalChars = 0;

      this.fieldEls.forEach((field, i) => {
        const input  = field.querySelector('[data-ctp-input]');
        if (!input) return;

        const text   = input.value;
        const maxLen = parseInt(input.getAttribute('maxlength') || '200', 10);
        const charges = field.dataset.charge === 'true';

        if (charges) totalChars += this._billableChars(text);

        // Character counter (only shown on charged fields)
        const counter = field.querySelector('[data-ctp-counter]');
        if (counter) {
          const left = maxLen - text.length;
          counter.textContent = left + (left === 1 ? ' character left' : ' characters left');
          counter.classList.toggle('ctp-counter--near', left <= Math.ceil(maxLen * 0.1) && left > 0);
          counter.classList.toggle('ctp-counter--full',  left <= 0);
        }

        if (this.hiddenProps[i]) this.hiddenProps[i].value = text;
      });

      // Set active flag BEFORE _setQty so unit-price.js render sees it immediately
      if (this.productInfo) {
        this.productInfo.setAttribute('data-ctp-active', '');
        this.productInfo.dataset.ctpChars = String(totalChars);
      }

      // Letter count drives quantity — AIOD tiers apply automatically
      this._setQty(Math.max(1, totalChars));

      // Keep Letters property in sync for the cart line item attribute
      if (this.lettersProp) this.lettersProp.value = String(totalChars);

      // Multi-color variant: flip Color Type SKU when a trigger field has text
      this._syncMulticolorVariant();

      // Update the price row label and unit label
      this._updatePriceLabel(totalChars);
      this._updateMinimumChargeNote(totalChars);

      const unitEl = (this.productInfo && this.productInfo.querySelector('[data-cs-unit]')) || document.querySelector('[data-cs-unit]');
      if (unitEl) {
        requestAnimationFrame(() => this._patchUnitLabel(unitEl));
      }
    }

    // ── Multi-color variant ───────────────────────────────────────

    // Sets the product's hidden Color Type variant option to its multi-color
    // value whenever a trigger field has text, and back to single-color when
    // they're all empty.
    //
    // We deliberately do NOT dispatch a `change` to <variant-selects>: that runs
    // Dawn's onVariantChange -> renderProductInfo(), which re-fetches the section
    // and re-renders media / the add button. Single and Multi are visually
    // identical (same price, same image, both available), so that round-trip only
    // caused a flash, a media/layout shift, and a transient "out of stock" while
    // currentVariant was recomputed. Instead we set the hidden control's value
    // (so Dawn stays consistent if the shopper changes other options) and write
    // the matching variant id straight into the form ourselves.
    _syncMulticolorVariant() {
      const optionName = (this.root.dataset.multicolorOption || '').trim();
      if (!optionName) return; // feature disabled / no Color Type option

      const vs = (this.productInfo || document).querySelector('variant-selects');
      if (!vs) return; // product has no variants (nothing to switch)

      const wantsMulti = this.fieldEls.some(field => {
        if (field.dataset.multicolorTrigger !== 'true') return false;
        const input = field.querySelector('[data-ctp-input]');
        return input && input.value.trim() !== '';
      });

      const target = wantsMulti
        ? (this.root.dataset.multicolorMulti || 'Multi Color')
        : (this.root.dataset.multicolorSingle || 'Single Color');

      // Locate the Color Type control (dropdown <select> or pill/swatch radios).
      let wrapper = vs.querySelector('[data-option-name="' + optionName + '"]');
      if (!wrapper) {
        const sel = vs.querySelector('select[name="options[' + optionName + ']"]');
        if (sel) wrapper = sel.closest('.product-form__input');
      }
      if (!wrapper) return; // no Color Type option on this product

      const select = wrapper.querySelector('select');
      if (select) {
        if (!Array.from(select.options).some(o => o.value === target)) return; // not offered
        if (select.value !== target) select.value = target;
      } else {
        const radios = Array.from(wrapper.querySelectorAll('input[type="radio"]'));
        const targetRadio = radios.find(r => r.value === target);
        if (!targetRadio) return; // value not offered on this product
        targetRadio.checked = true; // same-name radio group auto-unchecks the sibling
      }

      this._applyVariantIdFromSelection(vs);
    }

    // Reads the currently selected option values from <variant-selects>, finds the
    // matching variant in its embedded JSON, and writes that id into the product
    // form — without triggering Dawn's variant-change re-render.
    _applyVariantIdFromSelection(vs) {
      if (!this.productForm) return;

      if (!this._mcVariants) {
        const dataEl = vs.querySelector('script[type="application/json"]');
        if (!dataEl) return;
        try { this._mcVariants = JSON.parse(dataEl.textContent); } catch (e) { return; }
      }
      if (!Array.isArray(this._mcVariants)) return;

      const selected = Array.from(vs.querySelectorAll('select, fieldset')).map(el => {
        if (el.tagName === 'SELECT') return el.value;
        const checked = el.querySelector('input:checked');
        return checked ? checked.value : null;
      });

      const match = this._mcVariants.find(v =>
        Array.isArray(v.options) &&
        v.options.length === selected.length &&
        v.options.every((opt, i) => opt === selected[i])
      );
      if (!match) return;

      const idInput = this.productForm.querySelector('input[name="id"]');
      if (idInput && idInput.value !== String(match.id)) {
        idInput.value = String(match.id);
      }
    }

    // ── Validation ────────────────────────────────────────────────

    _validateSubmit(e) {
      this.update();

      let hasError = false;
      this.fieldEls.forEach(field => {
        const input    = field.querySelector('[data-ctp-input]');
        const errorEl  = field.querySelector('[data-ctp-error]');
        const required = field.dataset.required === 'true';
        if (!input) return;

        const fail = required && !input.value.trim();
        input.classList.toggle('ctp-input--error', fail);
        if (errorEl) {
          errorEl.textContent = fail ? 'This field is required.' : '';
          errorEl.classList.toggle('ctp-error--visible', fail);
        }
        if (fail && !hasError) {
          hasError = true;
          input.focus();
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      if (hasError) e.detail.blocked = true;
    }
  }

  // ── Boot ──────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('[data-ctp-section]').forEach(el => {
      if (el._ctpInit) return;
      el._ctpInit = true;
      new CustomTextPricing(el);
    });
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

})();
